// src/components/VerificationScreen.js
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useLocation } from "react-router-dom";
import UserService from "../lib/UserService";
import googleIcon from "../assets/images/google.svg";
import PhoneVerificationModal from "./PhoneVerificationModal";
import ReferrerManager from '../utils/ReferrerManager';
import analyticsService from "../lib/AnalyticsService";
import { useReferrer } from "../hooks/useReferrer";
import { Lock } from "lucide-react";


const Verification = ({ onComplete, onFullApp }) => {
  const location = useLocation();
  const {
    loginWithGoogle,
    sendSmsCode,
    verifySmsCode,
    loading: authLoading,
    error: authError,
    resetRecaptcha,
    user,
    firebaseUser,
    getIdToken,
  } = useAuth();

  const [verificationMethod, setVerificationMethod] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serverUserData, setServerUserData] = useState(null);
  const recaptchaRef = useRef(null);
  const referral = ReferrerManager.getReferrerWithSubdomainFallback();
  const { displayConfig, referrer, isLoading: referrerLoading } = useReferrer();
  const isWaters = referrer === "waterscarwash";


  // Update error message when auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const getOrCreateUserOnServer = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      // Get the ID token
      const token = await getIdToken(true);

      // Prepare additional user data to send
      const additionalData = {
        // You can add any additional data from your app state here
        email: user.email,
        phoneNumber: user.phoneNumber,
        displayName: user.displayName,
        // Add any other user data that might be available in your session
        ...JSON.parse(sessionStorage.getItem("userData") || "{}"),
        surveyData: JSON.parse(sessionStorage.getItem("surveyData") || "{}"),
      };

      // Call the API
      const userData = await UserService.getOrCreateUser(token, additionalData, referral);
      console.log("Server user data:", userData);

      // Store the server user data
      setServerUserData(userData);
      sessionStorage.setItem("userData", JSON.stringify(userData));

      return userData;
    } catch (err) {
      console.error("Error getting/creating user on server:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Check if user is already authenticated
  useEffect(() => {
    const handleAuthentication = async () => {
      if (user && firebaseUser) {
        try {
          // Call API to get or create user
          const serverUser = await getOrCreateUserOnServer();

          if (serverUser) {
            // Store verification data in sessionStorage
            const verificationData = {
              method: user.phoneNumber ? "phone" : "google",
              userId: user.id,
              serverId: serverUser.id || serverUser._id, // Depending on your API response
              verifiedAt: new Date().toISOString(),
            };
            sessionStorage.setItem(
              "verificationData",
              JSON.stringify(verificationData)
            );

            console.log("Verification complete");
            console.log(serverUser);
            analyticsService.alias(user.uid);
            // Then proceed to next step
            onComplete(verificationData);
          }
        } catch (err) {
          console.error("Authentication handling error:", err);
          setError(err.message);
        }
      }
    };
    // Run the authentication handler
    handleAuthentication();
  }, [user, firebaseUser]);

  const handleGoogleAuth = async () => {
    try {
      setError(null);
      sessionStorage.setItem("userLoginType", "Email");
      const user = await loginWithGoogle();

      if (user) {
        // Log the successful authentication
        console.log("Google Auth successful:", user.uid);
        // The useEffect above will handle API call and navigation
      }
    } catch (err) {
      setError("Google authentication failed. Please try again.");
    }
  };

  const handlePhoneSubmit = async () => {
    try {
      if (!phoneNumber.match(/^\d{10}$/)) {
        throw new Error("Please enter a valid 10-digit phone number");
      }
      sessionStorage.setItem("userLoginType", "Phone");
      // Format phone number for international format (assuming US)
      const formattedPhone = `+1${phoneNumber}`;
      console.error(formattedPhone);
      // Make sure recaptchaRef.current exists
      if (!recaptchaRef.current) {
        throw new Error("reCAPTCHA container not found");
      }
      console.error(formattedPhone);

      // Pass the DOM element instead of the string
      const success = await sendSmsCode(formattedPhone, recaptchaRef.current);
      // Send SMS verification code
      if (success) {
        console.log("SMS sent to:", formattedPhone);
        setOtpSent(true);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
      resetRecaptcha();
    }
  };

  const handleOtpSubmit = async (code) => {
    try {
      if (!code.match(/^\d{6}$/)) {
        throw new Error("Please enter a valid 6-digit OTP");
      }

      const user = await verifySmsCode(code);

      if (user) {
        console.log("Phone verification successful:", user.uid);
        // The useEffect will handle API call and navigation
      } else {
        throw new Error("Verification failed. Please try again.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const renderPhoneVerification = () => {
    if (!otpSent) {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-base text-darkGrey mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) =>
                setPhoneNumber(e.target.value.replace(/\D/g, ""))
              }
              placeholder="1234567890"
              className="w-full py-3 px-2 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={10}
            />
          </div>
          <div id="recaptcha-container" ref={recaptchaRef}></div>
          <button
            onClick={handlePhoneSubmit}
            disabled={authLoading || loading || !phoneNumber}
            className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors disabled:bg-none disabled:bg-gray-300"
          >
            {authLoading || loading ? "Sending..." : "Send verification code"}
            {/* <ArrowRight className="w-4 h-4" /> */}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Verification Code
          </label>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="Enter 6-digit code"
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={6}
          />
        </div>
        <button
          onClick={handleOtpSubmit}
          disabled={authLoading || loading}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {authLoading || loading ? "Verifying..." : "Verify Code"}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header Section */}
      <div className="text-center">
        <h1 className="text-xl uppercase font-bold mb-2">
          {isWaters
            ? "One quick step — then we find your best rate"
            : "Create Your Ownli Account"}
        </h1>
        <p className="text-lg text-darkGrey">
          {isWaters
            ? "Waters has partnered with Ownli to find you real insurance savings. Your account is where your earnings go."
            : "Your account helps us build your profile and securely deliver your rewards! It only takes a moment."}
        </p>
      </div>

      {/* Trust badge - Waters only */}
      {isWaters && !referrerLoading && (
        <div className="flex items-center justify-center gap-3 py-3 px-4 bg-gray-50 rounded-xl border border-gray-200">
          {displayConfig.logoUrl && (
            <img src={displayConfig.logoUrl} alt="Waters Car Wash" className="w-8 h-8 object-contain" />
          )}
          <span className="text-sm text-gray-600 font-medium">Powered by Ownli</span>
          <div className="flex items-center gap-1 text-gray-500 text-sm">
            <Lock className="w-3.5 h-3.5" />
            <span>Own Your Data</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {(authLoading || loading) && !verificationMethod && (
        <Card>
          <CardContent className="py-6 flex justify-center">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Verifying your identity...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verification Methods */}
      {/* {!verificationMethod && !(authLoading || loading) && (
        <Card>
          <CardHeader>
            <CardTitle>Choose sign up method</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <button
              onClick={() => setVerificationMethod("google")}
              className="w-full flex items-center gap-3 p-4 border rounded-lg hover:border-blue-300 transition-colors"
              disabled={authLoading || loading}
            >
              <Mail className="w-5 h-5 text-red-500" />
              <div className="text-left">
                <p className="font-medium">Sign up with Google</p>
                <p className="text-sm text-gray-500">
                  Create your account using Google
                </p>
              </div>
            </button>

            <button
              onClick={() => setVerificationMethod("phone")}
              className="w-full flex items-center gap-3 p-4 border rounded-lg hover:border-blue-300 transition-colors"
              disabled={authLoading || loading}
            >
              <Smartphone className="w-5 h-5 text-blue-500" />
              <div className="text-left">
                <p className="font-medium">Sign up with SMS</p>
                <p className="text-sm text-gray-500">
                  Create your account with phone verification
                </p>
              </div>
            </button>
          </CardContent>
        </Card>
      )} */}

      {/* Selected Verification Method */}
      {/* {verificationMethod && (
        <Card>
          <CardHeader>
            <CardTitle>
              {verificationMethod === "google"
                ? "Sign up with Google"
                : "Sign up with SMS"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {verificationMethod === "google" ? (
              <button
                onClick={handleGoogleAuth}
                disabled={authLoading || loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {authLoading || loading ? "Connecting..." : "Create Account"}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              renderPhoneVerification()
            )}
          </CardContent>
        </Card>
      )} */}
      {isWaters ? (
        <>
          {/* Waters: Google PRIMARY */}
          <button
            type="button"
            className="flex items-center w-full p-4 border border-grey rounded-lg shadow-sm hover:bg-gray-100 transition"
            onClick={handleGoogleAuth}
          >
            <img className="mr-4" src={googleIcon} alt="Google" />
            <div className="text-left">
              <p className="text-lg font-medium text-gray-900">Sign up with Google</p>
              <p className="text-sm text-gray-500">Verify using your Google account</p>
            </div>
          </button>

          <div className="flex items-center my-6">
            <hr className="flex-grow border-t border-gray-300" />
            <span className="mx-4 text-gray-500">or</span>
            <hr className="flex-grow border-t border-gray-300" />
          </div>

          {renderPhoneVerification()}

          <p className="text-center text-sm text-gray-500 mt-2">
            No spam. No hidden charges.
          </p>
        </>
      ) : (
        <>
          {/* Default: Phone PRIMARY */}
          {renderPhoneVerification()}

          <div className="flex items-center my-6">
            <hr className="flex-grow border-t border-gray-300" />
            <span className="mx-4 text-gray-500">or</span>
            <hr className="flex-grow border-t border-gray-300" />
          </div>

          <button
            type="button"
            className="flex items-center w-full p-4 border border-grey rounded-lg shadow-sm hover:bg-gray-100 transition"
            onClick={handleGoogleAuth}
          >
            <img className="mr-4" src={googleIcon} alt="Google" />
            <div className="text-left">
              <p className="text-lg font-medium text-gray-900">Sign up with Google</p>
              <p className="text-sm text-gray-500">Verify using your Google account</p>
            </div>
          </button>
        </>
      )}

      {/* Phone Verification Modal using Radix UI */}
      <PhoneVerificationModal
        authLoading={authLoading}
        loading={loading}
        open={otpSent}
        onOpenChange={(open) => {
          if (!open) setOtpSent(false);
        }}
        onVerify={async (code) => {
          await handleOtpSubmit(code);
        }}
        onResend={async () => {
          await handlePhoneSubmit();
        }}
        error={error && (error.toLowerCase().includes('otp') || error.toLowerCase().includes('code') || error.toLowerCase().includes('verification')) ? error : ''}
      />

      {/* Error Display */}
      {error && (
        <div className="text-red-600 text-sm p-2 bg-red-50 rounded-lg">
          {error}
        </div>
      )}

      {/* Legal Copy */}
      {/* <div className="text-center text-sm text-gray-600">
        By continuing, you agree to Ownli's{" "}
        <a
          href="https://www.ownli.co/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline inline-flex items-center"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href="https://www.ownli.co/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline inline-flex items-center"
        >
          Privacy Policy
        </a>
        .
      </div> */}
    </div>
  );
};

export default Verification;
